import {
	Button,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@dune/ui";
import { formatPricing, roundTo2Digits } from "@dune/utils";
import {
	PropositionProductType,
	ReceiptTypeEnum,
	ReceiptType,
	propositionProductTypes,
	receiptTypeValues,
	receiptTypeLabels,
} from "@dune/vars";
import { zodResolver } from "@hookform/resolvers/zod";
import { PenIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";
import { useUpdateReceiptMutation } from "../../../api/queries/receipt.queries";
import {
	DoInitialCellMapping,
	MrtInitialCellMapping,
	Nullable,
	OfferPricingOutput,
	ReceiptExtendedInfo,
	TrcInitialCellMapping,
} from "@dune/types";
import { mapObject } from "@dune/utils";
import { ControlledPriceInput } from "../../../components/inputs/ControlledPriceInput";

interface UpdateReceiptData {
	id: number;
	reference: string;
	type: ReceiptType;
	productType: PropositionProductType;
	issueDate: string;
	duneManagementFeesHT?: number | null;
	duneManagementFeesTaxes?: number | null;
	duneManagementFeesTTC?: number | null;
	intermediaryManagementFeesHT?: number | null;
	intermediaryManagementFeesTaxes?: number | null;
	intermediaryManagementFeesTTC?: number | null;
	brokerCommission?: number | null;
	brokerRemuneration?: number | null;
	totalClientPrimeHT?: number | null;
	totalClientPrimeTaxes?: number | null;
	totalClientPrimeTTC?: number | null;
	agreedRateHT?: number | null;
	agreedRateTTC?: number | null;
	doDetails?: Nullable<Partial<OfferPricingOutput<"DO", "INITIAL">>>;
	mrtDetails?: Nullable<Partial<OfferPricingOutput<"MRT", "INITIAL">>>;
	trcDetails?: Nullable<Partial<OfferPricingOutput<"TRC", "INITIAL">>>;
}

const normalizeDecimalTextToStringNumber = (input: string) =>
	input
		.trim()
		.replace(/\u00A0|\u202F/g, " ") // NBSP & thin NBSP -> normal space
		.replace(/\s/g, "") // remove all spaces
		.replace(",", "."); // allow comma as decimal separator

export const stringToNumber = (
	value: string | undefined | null,
): number | undefined | null => {
	if (!value || value.trim() === "") return null;
	const normalized = normalizeDecimalTextToStringNumber(value);
	const num = Number(normalized);
	return Number.isNaN(num) ? undefined : num;
};

const floatRoundedToPriceString = (
	value: number | null | undefined,
): string => {
	if (!value) return "0";
	return String(roundTo2Digits(value));
};

const updateReceiptFormSchema = z.object({
	reference: z.string().min(1, "La référence est obligatoire"),
	type: z.enum(ReceiptTypeEnum, {
		error: "Le type est obligatoire",
	}),
	productType: z.enum(propositionProductTypes, {
		error: "Le type de produit est obligatoire",
	}),
	issueDate: z.string().min(1, "La date d'émission est obligatoire"),
	...mapObject(DoInitialCellMapping.output, () => z.string().optional()),
	...mapObject(MrtInitialCellMapping.output, () => z.string().optional()),
	...mapObject(TrcInitialCellMapping.output, () => z.string().optional()),
});

type UpdateReceiptFormSchema = z.infer<typeof updateReceiptFormSchema>;

const zAllInitialPricingFields = z.object({
	...mapObject(DoInitialCellMapping.output, () => z.string().optional()),
	...mapObject(MrtInitialCellMapping.output, () => z.string().optional()),
	...mapObject(TrcInitialCellMapping.output, () => z.string().optional()),
});
type AllInitialPricingFields = z.infer<typeof zAllInitialPricingFields>;

const getDefaultValuesFromReceipt = (
	receipt: ReceiptExtendedInfo,
): UpdateReceiptFormSchema => {
	return {
		reference: receipt.reference,
		type: receipt.type,
		productType: receipt.productType,
		issueDate: receipt.issueDate.toString().split("T")[0],
		// Common fields
		duneManagementFeesHT: floatRoundedToPriceString(
			receipt.duneManagementFeesHT,
		),
		duneManagementFeesTaxes: floatRoundedToPriceString(
			receipt.duneManagementFeesTaxes,
		),
		duneManagementFeesTTC: floatRoundedToPriceString(
			receipt.duneManagementFeesTTC,
		),
		intermediaryManagementFeesHT: floatRoundedToPriceString(
			receipt.intermediaryManagementFeesHT,
		),
		intermediaryManagementFeesTaxes: floatRoundedToPriceString(
			receipt.intermediaryManagementFeesTaxes,
		),
		intermediaryManagementFeesTTC: floatRoundedToPriceString(
			receipt.intermediaryManagementFeesTTC,
		),
		brokerCommission: floatRoundedToPriceString(receipt.brokerCommission),
		brokerRemuneration: floatRoundedToPriceString(receipt.brokerRemuneration),
		agreedRateHT: receipt.agreedRateHT
			? floatRoundedToPriceString(receipt.agreedRateHT * 100)
			: "0",
		agreedRateTTC: receipt.agreedRateTTC
			? floatRoundedToPriceString(receipt.agreedRateTTC * 100)
			: "0",
		// DO fields
		...(receipt.productType === "DO" &&
			receipt.doDetails &&
			mapObject(receipt.doDetails, (value) =>
				floatRoundedToPriceString(value),
			)),
		// MRT fields
		...(receipt.productType === "MRT" &&
			receipt.mrtDetails &&
			mapObject(receipt.mrtDetails, (value) =>
				floatRoundedToPriceString(value),
			)),
		// TRC fields
		...(receipt.productType === "TRC" &&
			receipt.trcDetails &&
			mapObject(receipt.trcDetails, (value) =>
				floatRoundedToPriceString(value),
			)),
	};
};

export const toNumber = (value: string | undefined): number => {
	const result = stringToNumber(value);
	return result ?? 0;
};

export const calculateTotalPrimeFromReceiptFields = (
	productType: PropositionProductType,
	watchedValues: AllInitialPricingFields,
) => {
	let totalPrimeHT = 0;
	let totalTaxes = 0;
	let totalPrimeTTC = 0;

	// Calculate according to the product type
	switch (productType) {
		case "DO":
			totalPrimeHT =
				toNumber(watchedValues.doPrimeHT) +
				toNumber(watchedValues.existingDamagesPrimeHT) +
				toNumber(watchedValues.cnrPrimeHT) +
				toNumber(watchedValues.terrorismContributionPrimeHT);

			totalTaxes =
				toNumber(watchedValues.doPrimeTaxes) +
				toNumber(watchedValues.existingDamagesPrimeTaxes) +
				toNumber(watchedValues.cnrPrimeTaxes) +
				toNumber(watchedValues.terrorismContributionPrimeTaxes);

			totalPrimeTTC =
				toNumber(watchedValues.doPrimeTTC) +
				toNumber(watchedValues.existingDamagesPrimeTTC) +
				toNumber(watchedValues.cnrPrimeTTC) +
				toNumber(watchedValues.terrorismContributionPrimeTTC);
			break;

		case "MRT":
			totalPrimeHT =
				toNumber(watchedValues.trcPrimeHT) +
				toNumber(watchedValues.trcNaturalDisastersPrimeHT) +
				toNumber(watchedValues.terrorismContributionPrimeHT) +
				toNumber(watchedValues.rcmoPrimeHT) +
				toNumber(watchedValues.doPrimeHT);

			totalTaxes =
				toNumber(watchedValues.trcPrimeTaxes) +
				toNumber(watchedValues.trcNaturalDisastersPrimeTaxes) +
				toNumber(watchedValues.terrorismContributionPrimeTaxes) +
				toNumber(watchedValues.rcmoPrimeTaxes) +
				toNumber(watchedValues.doPrimeTaxes);

			totalPrimeTTC =
				toNumber(watchedValues.trcPrimeTTC) +
				toNumber(watchedValues.trcNaturalDisastersPrimeTTC) +
				toNumber(watchedValues.terrorismContributionPrimeTTC) +
				toNumber(watchedValues.rcmoPrimeTTC) +
				toNumber(watchedValues.doPrimeTTC);
			break;

		case "TRC":
			totalPrimeHT =
				toNumber(watchedValues.damageCoveragePrimeHT) +
				toNumber(watchedValues.damageCoverageFirePrimeHT) +
				toNumber(watchedValues.damageCoverageOtherDamagesPrimeHT) +
				toNumber(watchedValues.damageCoverageNaturalDisastersPrimeHT) +
				toNumber(watchedValues.rcmoPrimeHT) +
				toNumber(watchedValues.terrorismContributionPrimeTaxes);

			totalTaxes =
				toNumber(watchedValues.damageCoveragePrimeTaxes) +
				toNumber(watchedValues.damageCoverageFirePrimeTaxes) +
				toNumber(watchedValues.damageCoverageOtherDamagesPrimeTaxes) +
				toNumber(watchedValues.damageCoverageNaturalDisastersPrimeTaxes) +
				toNumber(watchedValues.rcmoPrimeTaxes) +
				toNumber(watchedValues.terrorismContributionPrimeTaxes);

			totalPrimeTTC =
				toNumber(watchedValues.damageCoveragePrimeTTC) +
				toNumber(watchedValues.damageCoverageFirePrimeTTC) +
				toNumber(watchedValues.damageCoverageOtherDamagesPrimeTTC) +
				toNumber(watchedValues.damageCoverageNaturalDisastersPrimeTTC) +
				toNumber(watchedValues.rcmoPrimeTTC) +
				toNumber(watchedValues.terrorismContributionPrimeTTC);
			break;
	}

	// Add management fees for all types
	totalPrimeHT +=
		toNumber(watchedValues.duneManagementFeesHT) +
		toNumber(watchedValues.intermediaryManagementFeesHT);

	totalTaxes +=
		toNumber(watchedValues.duneManagementFeesTaxes) +
		toNumber(watchedValues.intermediaryManagementFeesTaxes);

	totalPrimeTTC +=
		toNumber(watchedValues.duneManagementFeesTTC) +
		toNumber(watchedValues.intermediaryManagementFeesTTC);

	return { totalPrimeHT, totalTaxes, totalPrimeTTC };
};

interface UpdateReceiptModalProps {
	receipt: ReceiptExtendedInfo;
}

const UpdateReceiptModal = ({ receipt }: UpdateReceiptModalProps) => {
	const [open, setOpen] = useState(false);
	const updateReceiptMutation = useUpdateReceiptMutation();

	const form = useForm<UpdateReceiptFormSchema>({
		resolver: zodResolver(updateReceiptFormSchema),
		defaultValues: getDefaultValuesFromReceipt(receipt),
	});
	const { reset } = form;

	useEffect(() => {
		if (receipt) {
			reset(getDefaultValuesFromReceipt(receipt));
		}
	}, [receipt, reset]);

	const watchedValues = form.watch();

	const {
		totalPrimeHT: computedTotalPrimeHT,
		totalTaxes: computedTotalTaxes,
		totalPrimeTTC: computedTotalPrimeTTC,
	} = useMemo(
		() =>
			calculateTotalPrimeFromReceiptFields(receipt.productType, watchedValues),
		[watchedValues, receipt.productType],
	);

	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen) {
			form.reset(getDefaultValuesFromReceipt(receipt));
		}
		setOpen(nextOpen);
	};

	const onSubmit = async (values: UpdateReceiptFormSchema) => {
		try {
			const agreedRateHT = stringToNumber(values.agreedRateHT);
			const agreedRateTTC = stringToNumber(values.agreedRateTTC);

			const baseReceiptData: UpdateReceiptData = {
				id: receipt.id,
				reference: values.reference,
				type: values.type,
				productType: values.productType,
				issueDate: values.issueDate,
				duneManagementFeesHT: stringToNumber(values.duneManagementFeesHT),
				duneManagementFeesTaxes: stringToNumber(values.duneManagementFeesTaxes),
				duneManagementFeesTTC: stringToNumber(values.duneManagementFeesTTC),
				intermediaryManagementFeesHT: stringToNumber(
					values.intermediaryManagementFeesHT,
				),
				intermediaryManagementFeesTaxes: stringToNumber(
					values.intermediaryManagementFeesTaxes,
				),
				intermediaryManagementFeesTTC: stringToNumber(
					values.intermediaryManagementFeesTTC,
				),
				brokerCommission: stringToNumber(values.brokerCommission),
				brokerRemuneration: stringToNumber(values.brokerRemuneration),
				totalClientPrimeHT: computedTotalPrimeHT,
				totalClientPrimeTaxes: computedTotalTaxes,
				totalClientPrimeTTC: computedTotalPrimeTTC,
				agreedRateHT: agreedRateHT ? agreedRateHT / 100 : null,
				agreedRateTTC: agreedRateTTC ? agreedRateTTC / 100 : null,
			};

			// Add details specific to the type
			let receiptData: UpdateReceiptData = { ...baseReceiptData };

			switch (receipt.productType) {
				case "DO":
					receiptData = {
						...receiptData,
						doDetails: {
							doPrimeHT: stringToNumber(values.doPrimeHT),
							doPrimeTaxes: stringToNumber(values.doPrimeTaxes),
							doPrimeTTC: stringToNumber(values.doPrimeTTC),
							existingDamagesPrimeHT: stringToNumber(
								values.existingDamagesPrimeHT,
							),
							existingDamagesPrimeTaxes: stringToNumber(
								values.existingDamagesPrimeTaxes,
							),
							existingDamagesPrimeTTC: stringToNumber(
								values.existingDamagesPrimeTTC,
							),
							cnrPrimeHT: stringToNumber(values.cnrPrimeHT),
							cnrPrimeTaxes: stringToNumber(values.cnrPrimeTaxes),
							cnrPrimeTTC: stringToNumber(values.cnrPrimeTTC),
							terrorismContributionPrimeHT: stringToNumber(
								values.terrorismContributionPrimeHT,
							),
							terrorismContributionPrimeTaxes: stringToNumber(
								values.terrorismContributionPrimeTaxes,
							),
							terrorismContributionPrimeTTC: stringToNumber(
								values.terrorismContributionPrimeTTC,
							),
						},
					};
					break;

				case "MRT":
					receiptData = {
						...receiptData,
						mrtDetails: {
							trcPrimeHT: stringToNumber(values.trcPrimeTTC),
							trcPrimeTaxes: stringToNumber(values.trcPrimeTaxes),
							trcPrimeTTC: stringToNumber(values.trcPrimeTTC),
							trcNaturalDisastersPrimeHT: stringToNumber(
								values.trcNaturalDisastersPrimeHT,
							),
							trcNaturalDisastersPrimeTaxes: stringToNumber(
								values.trcNaturalDisastersPrimeTaxes,
							),
							trcNaturalDisastersPrimeTTC: stringToNumber(
								values.trcNaturalDisastersPrimeTTC,
							),
							trcTerrorismContributionPrimeHT: stringToNumber(
								values.trcTerrorismContributionPrimeHT,
							),
							trcTerrorismContributionPrimeTaxes: stringToNumber(
								values.trcTerrorismContributionPrimeTaxes,
							),
							trcTerrorismContributionPrimeTTC: stringToNumber(
								values.trcTerrorismContributionPrimeTTC,
							),
							rcmoPrimeHT: stringToNumber(values.rcmoPrimeHT),
							rcmoPrimeTaxes: stringToNumber(values.rcmoPrimeTaxes),
							rcmoPrimeTTC: stringToNumber(values.rcmoPrimeTTC),
							doCnrPrimeHT: stringToNumber(values.doCnrPrimeHT),
							doCnrPrimeTaxes: stringToNumber(values.doCnrPrimeTaxes),
							doCnrPrimeTTC: stringToNumber(values.doCnrPrimeTTC),
						},
					};
					break;

				case "TRC":
					receiptData = {
						...receiptData,
						trcDetails: {
							damageCoveragePrimeHT: stringToNumber(
								values.damageCoveragePrimeHT,
							),
							damageCoveragePrimeTaxes: stringToNumber(
								values.damageCoveragePrimeTaxes,
							),
							damageCoveragePrimeTTC: stringToNumber(
								values.damageCoveragePrimeTTC,
							),
							damageCoverageFirePrimeHT: stringToNumber(
								values.damageCoverageFirePrimeHT,
							),
							damageCoverageFirePrimeTaxes: stringToNumber(
								values.damageCoverageFirePrimeTaxes,
							),
							damageCoverageFirePrimeTTC: stringToNumber(
								values.damageCoverageFirePrimeTTC,
							),
							damageCoverageOtherDamagesPrimeHT: stringToNumber(
								values.damageCoverageOtherDamagesPrimeHT,
							),
							damageCoverageOtherDamagesPrimeTaxes: stringToNumber(
								values.damageCoverageOtherDamagesPrimeTaxes,
							),
							damageCoverageOtherDamagesPrimeTTC: stringToNumber(
								values.damageCoverageOtherDamagesPrimeTTC,
							),
							damageCoverageNaturalDisastersPrimeHT: stringToNumber(
								values.damageCoverageNaturalDisastersPrimeHT,
							),
							damageCoverageNaturalDisastersPrimeTaxes: stringToNumber(
								values.damageCoverageNaturalDisastersPrimeTaxes,
							),
							damageCoverageNaturalDisastersPrimeTTC: stringToNumber(
								values.damageCoverageNaturalDisastersPrimeTTC,
							),
							rcmoPrimeHT: stringToNumber(values.rcmoPrimeHT),
							rcmoPrimeTaxes: stringToNumber(values.rcmoPrimeTaxes),
							rcmoPrimeTTC: stringToNumber(values.rcmoPrimeTTC),
							terrorismContributionPrimeHT: stringToNumber(
								values.terrorismContributionPrimeHT,
							),
							terrorismContributionPrimeTaxes: stringToNumber(
								values.terrorismContributionPrimeTaxes,
							),
							terrorismContributionPrimeTTC: stringToNumber(
								values.terrorismContributionPrimeTTC,
							),
						},
					};
					break;
			}

			await updateReceiptMutation.mutateAsync(receiptData);

			toast.success("La quittance a été modifiée avec succès");
			setOpen(false);
		} catch (error) {
			// Handle different error types
			if (error instanceof Error) {
				if (error.message.includes("existe déjà")) {
					toast.error("Une quittance avec cette référence existe déjà");
				} else if (error.message.includes("introuvable")) {
					toast.error("La quittance spécifiée est introuvable");
				} else {
					toast.error(
						`Erreur lors de la modification de la quittance: ${error.message}`,
					);
				}
			} else {
				toast.error(
					"Une erreur inattendue s'est produite lors de la modification de la quittance",
				);
			}
		}
	};

	const renderProductSpecificFields = () => {
		switch (receipt.productType) {
			case "DO":
				return (
					<>
						{/* Assurance Dommages Ouvrage */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Assurance DO HT"
								name="doPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="doPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Assurance DO TTC"
								name="doPrimeTTC"
							/>
						</div>

						{/* Dommages aux Existants */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Dommages aux Existants HT"
								name="existingDamagesPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="existingDamagesPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Dommages aux Existants TTC"
								name="existingDamagesPrimeTTC"
							/>
						</div>

						{/* CNR */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="CNR HT"
								name="cnrPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="cnrPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="CNR TTC"
								name="cnrPrimeTTC"
							/>
						</div>

						{/* Contribution Attentats */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Contribution Attentats HT"
								name="terrorismContributionPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="terrorismContributionPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Contribution Attentats TTC"
								name="terrorismContributionPrimeTTC"
							/>
						</div>
					</>
				);

			case "MRT":
				return (
					<>
						{/* Tous Risques Chantier */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Tous Risques Chantier HT"
								name="trcPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="trcPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Tous Risques Chantier TTC"
								name="trcPrimeTTC"
							/>
						</div>

						{/* Catastrophes Naturelles */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Catastrophes Naturelles HT"
								name="trcNaturalDisastersPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="trcNaturalDisastersPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Catastrophes Naturelles TTC"
								name="trcNaturalDisastersPrimeTTC"
							/>
						</div>

						{/* Contribution Attentats */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Contribution Attentats HT"
								name="trcTerrorismContributionPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="trcTerrorismContributionPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Contribution Attentats TTC"
								name="trcTerrorismContributionPrimeTTC"
							/>
						</div>

						{/* Responsabilité Civile du Maître d'Ouvrage */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="RC Maître d'Ouvrage HT"
								name="rcmoPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="rcmoPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="RC Maître d'Ouvrage TTC"
								name="rcmoPrimeTTC"
							/>
						</div>

						{/* Dommages Ouvrages (Incluant la CNR) */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="DO (Incluant CNR) HT"
								name="doCnrPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="doCnrPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="DO (Incluant CNR) TTC"
								name="doCnrPrimeTTC"
							/>
						</div>
					</>
				);

			case "TRC":
				return (
					<>
						{/* Garantie de Dommages */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Garantie de Dommages HT"
								name="damageCoveragePrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="damageCoveragePrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Garantie de Dommages TTC"
								name="damageCoveragePrimeTTC"
							/>
						</div>

						{/* Dont Incendie */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Dont Incendie HT"
								name="damageCoverageFirePrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="damageCoverageFirePrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Dont Incendie TTC"
								name="damageCoverageFirePrimeTTC"
							/>
						</div>

						{/* Dont Autres Dommages */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Dont Autres Dommages HT"
								name="damageCoverageOtherDamagesPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="damageCoverageOtherDamagesPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Dont Autres Dommages TTC"
								name="damageCoverageOtherDamagesPrimeTTC"
							/>
						</div>

						{/* Dont Catastrophes Naturelles */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Dont Catastrophes Naturelles HT"
								name="damageCoverageNaturalDisastersPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="damageCoverageNaturalDisastersPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Dont Catastrophes Naturelles TTC"
								name="damageCoverageNaturalDisastersPrimeTTC"
							/>
						</div>

						{/* Responsabilité Civile */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="RC Maître d'Ouvrage HT"
								name="rcmoPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="rcmoPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="RC Maître d'Ouvrage TTC"
								name="rcmoPrimeTTC"
							/>
						</div>

						{/* Contribution Attentats */}
						<div className="grid grid-cols-3 gap-4">
							<ControlledPriceInput
								control={form.control}
								label="Contribution Attentats HT"
								name="terrorismContributionPrimeHT"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Taxes"
								name="terrorismContributionPrimeTaxes"
							/>
							<ControlledPriceInput
								control={form.control}
								label="Contribution Attentats TTC"
								name="terrorismContributionPrimeTTC"
							/>
						</div>
					</>
				);

			default:
				return (
					<div className="text-center py-8 text-gray-500">
						<p>
							Les détails pour ce type de produit ne sont pas encore disponibles
							dans ce formulaire.
						</p>
					</div>
				);
		}
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button>
					<PenIcon />
					Modifier
				</Button>
			</DialogTrigger>
			<DialogContent className="max-w-[1000px] max-h-[90vh] overflow-y-auto">
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
						<DialogHeader>
							<DialogTitle>
								Modifier une quittance - {receipt.productType}
							</DialogTitle>
						</DialogHeader>

						{/* Champs de base */}
						<div className="grid grid-cols-4 gap-4">
							<FormField
								control={form.control}
								name="reference"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Référence quittance</FormLabel>
										<FormControl>
											<Input placeholder="REF-001" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="type"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Type</FormLabel>
										<Select onValueChange={field.onChange} value={field.value}>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="Sélectionner" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{receiptTypeValues.map((type) => (
													<SelectItem key={type} value={type}>
														{receiptTypeLabels[type]}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="productType"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Type de Produit</FormLabel>
										<FormControl>
											<Input
												{...field}
												readOnly
												className="bg-gray-100"
												value={receipt.productType}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="issueDate"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Date d'émission</FormLabel>
										<FormControl>
											<Input type="date" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Champs spécifiques au produit */}
						<div className="space-y-6">
							<h3 className="text-lg font-semibold">
								Détails {receipt.productType}
							</h3>
							{renderProductSpecificFields()}
						</div>

						{/* Frais de gestion */}
						<div className="space-y-4">
							<h3 className="text-lg font-semibold">Frais de Gestion</h3>

							{/* Frais DUNE */}
							<div className="grid grid-cols-3 gap-4">
								<ControlledPriceInput
									control={form.control}
									label="Frais de Gestion DUNE HT"
									name="duneManagementFeesHT"
								/>
								<ControlledPriceInput
									control={form.control}
									label="Taxes"
									name="duneManagementFeesTaxes"
								/>
								<ControlledPriceInput
									control={form.control}
									label="Frais de Gestion DUNE TTC"
									name="duneManagementFeesTTC"
								/>
							</div>

							{/* Frais apporteur */}
							<div className="grid grid-cols-3 gap-4">
								<ControlledPriceInput
									control={form.control}
									label="Frais apporteur HT"
									name="intermediaryManagementFeesHT"
								/>
								<ControlledPriceInput
									control={form.control}
									label="Taxes"
									name="intermediaryManagementFeesTaxes"
								/>
								<ControlledPriceInput
									control={form.control}
									label="Frais apporteur TTC"
									name="intermediaryManagementFeesTTC"
								/>
							</div>
						</div>

						{/* Commissions */}
						<div className="space-y-4">
							<h3 className="text-lg font-semibold">Commissions</h3>
							<div className="grid grid-cols-2 gap-4">
								<ControlledPriceInput
									control={form.control}
									label="Commission Apporteur"
									name="brokerCommission"
								/>
								<ControlledPriceInput
									control={form.control}
									label="Rémunération Apporteur"
									name="brokerRemuneration"
								/>
							</div>
						</div>

						{/* Totaux */}
						<div className="bg-gray-50 p-4 rounded-lg">
							<h3 className="text-lg font-semibold mb-4">Récapitulatif</h3>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Total HT</TableHead>
										<TableHead>Total Taxes</TableHead>
										<TableHead>Total TTC</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									<TableRow>
										<TableCell className="font-bold">
											{formatPricing(computedTotalPrimeHT)}
										</TableCell>
										<TableCell className="font-bold">
											{formatPricing(computedTotalTaxes)}
										</TableCell>
										<TableCell className="font-bold">
											{formatPricing(computedTotalPrimeTTC)}
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</div>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => handleOpenChange(false)}
							>
								Annuler
							</Button>
							<Button type="submit" disabled={updateReceiptMutation.isPending}>
								{updateReceiptMutation.isPending
									? "Modification..."
									: "Valider"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default UpdateReceiptModal;
